/**
 * D3/SVG mobile fallback map — pure SVG renderer for low-memory devices.
 *
 * Uses Natural Earth projection with country boundaries loaded from
 * a TopoJSON world atlas. No WebGL dependency; zoom/pan via d3-zoom.
 */

import * as d3 from 'd3';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SVGMapOptions {
  container: HTMLElement;
  initialCenter?: [number, number];
  initialZoom?: number;
}

export interface SVGMarkerData {
  lat: number;
  lng: number;
  severity: string;
  label?: string;
}

export interface SVGMapInstance {
  setMarkers(markers: SVGMarkerData[]): void;
  setView(lat: number, lng: number, zoom: number): void;
  destroy(): void;
}

// ---------------------------------------------------------------------------
// Severity palette
// ---------------------------------------------------------------------------

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  info: '#3b82f6',
};

const DEFAULT_MARKER_COLOR = '#3b82f6';

// ---------------------------------------------------------------------------
// TopoJSON minimal types (avoids external topojson-client dependency)
// ---------------------------------------------------------------------------

interface TopoGeometry {
  type: string;
  geometries: unknown[];
  [key: string]: unknown;
}

interface Topology {
  type: 'Topology';
  objects: Record<string, TopoGeometry>;
  arcs: number[][][];
  transform?: {
    scale: [number, number];
    translate: [number, number];
  };
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// TopoJSON → GeoJSON conversion (minimal implementation)
// ---------------------------------------------------------------------------

function topologyArc(topology: Topology, arcIndex: number): [number, number][] {
  const arc = topology.arcs[arcIndex < 0 ? ~arcIndex : arcIndex];
  const coordinates: [number, number][] = [];
  let x = 0;
  let y = 0;

  for (const point of arc) {
    x += point[0];
    y += point[1];
    if (topology.transform) {
      coordinates.push([
        x * topology.transform.scale[0] + topology.transform.translate[0],
        y * topology.transform.scale[1] + topology.transform.translate[1],
      ]);
    } else {
      coordinates.push([x, y]);
    }
  }

  if (arcIndex < 0) {
    coordinates.reverse();
  }

  return coordinates;
}

function decodeArcs(topology: Topology, arcs: (number | number[])[]): [number, number][][] {
  const rings: [number, number][][] = [];
  for (const ring of arcs) {
    if (Array.isArray(ring)) {
      const coords: [number, number][] = [];
      for (const idx of ring as number[]) {
        const decoded = topologyArc(topology, idx);
        // Skip first point of subsequent arcs to avoid duplicates
        const start = coords.length > 0 ? 1 : 0;
        for (let i = start; i < decoded.length; i++) {
          coords.push(decoded[i]);
        }
      }
      rings.push(coords);
    } else {
      rings.push(topologyArc(topology, ring as number));
    }
  }
  return rings;
}

function topoFeature(
  topology: Topology,
  objectName: string,
): GeoJSON.FeatureCollection {
  const obj = topology.objects[objectName];
  if (!obj) {
    return { type: 'FeatureCollection', features: [] };
  }

  const features: GeoJSON.Feature[] = [];

  for (const geom of obj.geometries as Array<{
    type: string;
    arcs: unknown;
    properties?: Record<string, unknown>;
    id?: string | number;
  }>) {
    let geometry: GeoJSON.Geometry | null = null;

    if (geom.type === 'Polygon') {
      const rings = decodeArcs(topology, geom.arcs as number[][]);
      geometry = { type: 'Polygon', coordinates: rings };
    } else if (geom.type === 'MultiPolygon') {
      const polys = (geom.arcs as number[][][]).map((polyArcs) =>
        decodeArcs(topology, polyArcs),
      );
      geometry = { type: 'MultiPolygon', coordinates: polys };
    }

    if (geometry) {
      features.push({
        type: 'Feature',
        geometry,
        properties: geom.properties ?? { id: geom.id },
      });
    }
  }

  return { type: 'FeatureCollection', features };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WORLD_ATLAS_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const COUNTRY_FILL = '#1a1a2e';
const COUNTRY_STROKE = '#333344';
const OCEAN_BG = '#0a0a15';

const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 12;
const MARKER_RADIUS = 5;

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function initSVGMap(options: SVGMapOptions): SVGMapInstance {
  const { container, initialCenter, initialZoom } = options;

  const width = container.clientWidth || 960;
  const height = container.clientHeight || 500;

  // Projection
  const projection = d3
    .geoNaturalEarth1()
    .translate([width / 2, height / 2])
    .scale(width / 5.5);

  if (initialCenter) {
    projection.center(initialCenter);
  }

  if (initialZoom && initialZoom > 1) {
    projection.scale((width / 5.5) * initialZoom);
  }

  const pathGenerator = d3.geoPath(projection);

  // SVG root
  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .style('background', OCEAN_BG);

  const g = svg.append('g');

  // Ocean rect (behind countries)
  g.append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', OCEAN_BG);

  // Groups
  const countriesGroup = g.append('g').attr('class', 'countries');
  const markersGroup = g.append('g').attr('class', 'markers');

  // Zoom behavior
  const zoomBehavior = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([MIN_ZOOM_SCALE, MAX_ZOOM_SCALE])
    .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
      g.attr('transform', event.transform.toString());
    });

  svg.call(zoomBehavior);

  // Apply initial zoom if provided
  if (initialZoom && initialZoom > 1) {
    svg.call(
      zoomBehavior.transform,
      d3.zoomIdentity.translate(width / 2, height / 2).scale(initialZoom).translate(-width / 2, -height / 2),
    );
  }

  // Load country boundaries
  d3.json<Topology>(WORLD_ATLAS_URL).then((topology) => {
    if (!topology) return;

    const objectKey =
      'countries' in topology.objects
        ? 'countries'
        : Object.keys(topology.objects)[0];

    if (!objectKey) return;

    const countries = topoFeature(topology, objectKey);

    countriesGroup
      .selectAll<SVGPathElement, GeoJSON.Feature>('path')
      .data(countries.features)
      .join('path')
      .attr('d', (d) => pathGenerator(d) ?? '')
      .attr('fill', COUNTRY_FILL)
      .attr('stroke', COUNTRY_STROKE)
      .attr('stroke-width', 0.5);
  });

  // Marker state
  let currentMarkers: SVGMarkerData[] = [];

  function renderMarkers(): void {
    markersGroup
      .selectAll<SVGCircleElement, SVGMarkerData>('circle')
      .data(currentMarkers, (_d, i) => String(i))
      .join(
        (enter) =>
          enter
            .append('circle')
            .attr('cx', (d) => {
              const pt = projection([d.lng, d.lat]);
              return pt ? pt[0] : 0;
            })
            .attr('cy', (d) => {
              const pt = projection([d.lng, d.lat]);
              return pt ? pt[1] : 0;
            })
            .attr('r', MARKER_RADIUS)
            .attr('fill', (d) => SEVERITY_COLORS[d.severity] ?? DEFAULT_MARKER_COLOR)
            .attr('fill-opacity', 0.85)
            .attr('stroke', '#ffffff')
            .attr('stroke-width', 0.5),
        (update) =>
          update
            .attr('cx', (d) => {
              const pt = projection([d.lng, d.lat]);
              return pt ? pt[0] : 0;
            })
            .attr('cy', (d) => {
              const pt = projection([d.lng, d.lat]);
              return pt ? pt[1] : 0;
            })
            .attr('fill', (d) => SEVERITY_COLORS[d.severity] ?? DEFAULT_MARKER_COLOR),
        (exit) => exit.remove(),
      );

    // Tooltips via title elements
    markersGroup
      .selectAll<SVGCircleElement, SVGMarkerData>('circle')
      .select('title')
      .remove();

    markersGroup
      .selectAll<SVGCircleElement, SVGMarkerData>('circle')
      .filter((d) => d.label != null)
      .append('title')
      .text((d) => d.label ?? '');
  }

  // Public API
  function setMarkers(markers: SVGMarkerData[]): void {
    currentMarkers = markers;
    renderMarkers();
  }

  function setView(lat: number, lng: number, zoom: number): void {
    const point = projection([lng, lat]);
    if (!point) return;

    const scale = Math.max(MIN_ZOOM_SCALE, Math.min(MAX_ZOOM_SCALE, zoom));
    const transform = d3.zoomIdentity
      .translate(width / 2, height / 2)
      .scale(scale)
      .translate(-point[0], -point[1]);

    svg.transition().duration(750).call(zoomBehavior.transform, transform);
  }

  function destroy(): void {
    svg.remove();
    currentMarkers = [];
  }

  return { setMarkers, setView, destroy };
}
